import { SessionStatusData, ValidateSessionResponse } from '@/models/auth.models';
import { axiosClient } from '@/services/http';
import { Response } from '@/types/response';

const isWrappedSessionResponse = (
  data: SessionStatusData | Response<SessionStatusData>
): data is Response<SessionStatusData> => {
  return typeof data === 'object' && data !== null && 'success' in data;
};

/**
 * Session validation service
 */
export class SessionValidationService {
  /**
   * Validates a registration session ID
   * @param sessionId The session ID to validate
   * @returns Session validation response
   */
  async validateSession(sessionId: string): Promise<ValidateSessionResponse> {
    try {
      const response = await axiosClient.get<Response<SessionStatusData>>(
        `/session/status/${sessionId}`,
        { headers: { Authorization: undefined } }
      );

      const payload = response.data;

      if (isWrappedSessionResponse(payload)) {
        const { data, success, error } = payload;

        if (!success || !data) {
          return {
            isValid: false,
            errorMessage: error || 'Failed to validate session',
          };
        }

        return this.parseSessionData(data);
      }

      return this.parseSessionData(payload);
    } catch (error: unknown) {
      console.error('Session validation error:', error);

      const axiosError = error as { response?: { status?: number; data?: { error?: string } } };

      // Handle specific error cases
      if (axiosError.response?.status === 404) {
        return {
          isValid: false,
          errorMessage: 'Session not found',
        };
      }

      if (axiosError.response?.status === 400) {
        return {
          isValid: false,
          errorMessage: axiosError.response?.data?.error || 'Invalid session parameters',
        };
      }

      return {
        isValid: false,
        errorMessage: 'Failed to validate session. Please try again.',
      };
    }
  }

  private parseSessionData(data: SessionStatusData): ValidateSessionResponse {
    if (!data.isActive) {
      return {
        isValid: false,
        errorMessage: 'Session is not active',
      };
    }

    if (data.isExpired) {
      return {
        isValid: false,
        errorMessage: 'Session has expired',
      };
    }

    return {
      isValid: true,
      expiresAt: data.expiresAt,
      sessionData: data,
    };
  }
}

// Export singleton instance
export const sessionValidationService = new SessionValidationService();

/**
 * Close a registration session
 */
export const closeSession = async (institutionId: string, sessionId: string): Promise<void> => {
  await axiosClient.post(
    `/session/close/${encodeURIComponent(institutionId)}/${encodeURIComponent(sessionId)}`
  );
};

// Default export
export default sessionValidationService;
